"use client"
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useProfil } from "../../hooks/api/profil";
import { formatDate } from "../../lib/utils";

interface ProfilDetailProps {
  profilId: number;
  onBack: () => void;
  onEdit: () => void;
}

export function ProfilDetail({ profilId, onBack, onEdit }: ProfilDetailProps) {
  const { data: profilData, isLoading, error } = useProfil(profilId);
  const profil = profilData?.data;

  if (isLoading) {
    return <div className="flex justify-center p-4">Chargement des détails du profil...</div>;
  }

  if (error || !profil) {
    return (
      <div className="text-red-500 p-4">
        Une erreur est survenue lors du chargement du profil.
        <Button onClick={onBack} variant="outline" size="sm" className="ml-2">
          Retour
        </Button>
      </div>
    );
  }

  const renderInfoRow = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-3 py-3 border-b border-gray-100">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      <div className="col-span-2">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold">Détails du Profil</h2>
          <p className="text-gray-500 text-sm">
            Informations détaillées du profil sélectionné
          </p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={onBack} variant="outline" size="sm">
            Retour
          </Button>
          <Button onClick={onEdit} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
            Modifier
          </Button>
        </div>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold">{profil.name || "Sans nom"}</h3>
          <div>
            {profil.deleted ? (
              <Badge variant="destructive">Supprimé</Badge>
            ) : profil.is_init ? (
              <Badge variant="default">Actif</Badge>
            ) : (
              <Badge variant="secondary">Inactif</Badge>
            )}
          </div>
        </div>

        <div className="space-y-1">
          {renderInfoRow("ID", profil.id)}
          {renderInfoRow("Nom", profil.name || "-")}
          {renderInfoRow("Type de groupe", profil.groupe_type || "-")}
          {renderInfoRow("ID Droit", profil.droit_id)}
          {renderInfoRow("Statut",
            profil.deleted ? "Supprimé" : (profil.is_init ? "Actif" : "Inactif")
          )}
          {renderInfoRow("Date de création",
            profil.created_at ? formatDate(new Date(profil.created_at)) : "-"
          )}
          {renderInfoRow("Dernière mise à jour",
            profil.updated_at ? formatDate(new Date(profil.updated_at)) : "-"
          )}
        </div>
      </Card>

      <div className="flex justify-between pt-4">
        <Button onClick={onBack} variant="outline">
          Retour à la liste
        </Button>
        <Button onClick={onEdit} className="bg-emerald-600 hover:bg-emerald-700">
          Modifier ce profil
        </Button>
      </div>
    </div>
  );
}
